import { LogLevel } from './log-level';
import { Logger, LoggerBackend } from './logger';
import { LoggerConfiguration, WILDCARD_NAMESPACE } from './logger-configuration';

const DEFAULT_LOG_LEVEL = LogLevel.Warn;

export class LoggerImplementation implements Logger {
  private readonly logLevel: LogLevel = DEFAULT_LOG_LEVEL;

  constructor(
    public readonly configuration: LoggerConfiguration = { [WILDCARD_NAMESPACE]: DEFAULT_LOG_LEVEL },
    public readonly namespace = WILDCARD_NAMESPACE,
    private readonly backend: LoggerBackend = console,
  ) {
    // prevent empty string as namespace
    if (!this.namespace) {
      this.namespace = WILDCARD_NAMESPACE;
    }

    const parts = this.namespace.split('.');
    // add wildcard at the end to resolve account for wildcard configs
    parts.push(WILDCARD_NAMESPACE);
    let config: LoggerConfiguration | LogLevel | undefined = this.configuration;

    for (const part of parts) {
      config = part in config ? config[part] : config[WILDCARD_NAMESPACE];

      if (typeof config === 'number') {
        this.logLevel = config;
        break;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!config) {
        this.logLevel = DEFAULT_LOG_LEVEL;
        break;
      }
    }
  }

  getLogger(namespace?: string) {
    if (!namespace) {
      return this;
    }

    const prefix = 'foundation.';
    if (namespace.startsWith(prefix)) {
      namespace = namespace.slice(prefix.length);
    }
    return new LoggerImplementation(this.configuration, namespace, this.backend);
  }

  private log(level: LogLevel, message: string, ...context: unknown[]): void {
    if (this.isLoggingActiveForLevel(level)) {
      const prefixedMsg = `[${this.namespace}]: ${message}`;

      switch (level) {
        case LogLevel.Trace:
          this.backend.trace(prefixedMsg, ...context);
          break;
        case LogLevel.Debug:
          this.backend.debug(prefixedMsg, ...context);
          break;
        case LogLevel.Info:
          this.backend.info(prefixedMsg, ...context);
          break;
        case LogLevel.Warn:
          this.backend.warn(prefixedMsg, ...context);
          break;
        case LogLevel.Error:
          this.backend.error(prefixedMsg, ...context);
          break;
        case LogLevel.None:
          break;
      }
    }
  }

  getLevel(): LogLevel {
    return this.logLevel;
  }

  debug(message: string, ...context: unknown[]): void {
    this.log(LogLevel.Debug, message, ...context);
  }

  error(message: string, ...context: unknown[]): void {
    this.log(LogLevel.Error, message, ...context);
  }

  info(message: string, ...context: unknown[]): void {
    this.log(LogLevel.Info, message, ...context);
  }

  trace(message: string, ...context: unknown[]): void {
    this.log(LogLevel.Trace, message, ...context);
  }

  warn(message: string, ...context: unknown[]): void {
    this.log(LogLevel.Warn, message, ...context);
  }

  isLoggingActiveForLevel(logLevel: LogLevel): boolean {
    return logLevel <= this.logLevel;
  }
}
